/**
 * ============================================================================
 * BlogConnectModalForm — Controller (client)
 * ============================================================================
 * OWN MVC (not shared with HomeNew): connect-counsellor fires automatically
 * on every outcome (see src/utils/connectCounsellor.ts), no WebEngage on
 * success, always reveal programs on ID==='1', forceLeadResponse ID to "1",
 * dynamic pageType, no lead-check, ApplicationFormRedirectLoader owned here.
 * No JSX — view consumes the ViewModel.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import {
  LEAD_CHECK_SKIP_OTP,
  setLeadFormDegreeValue,
  setLeadFormProgramValue,
  setLeadFormInputValue,
} from "@/helpers/leadFormDegreeProgramFields";
import { useLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { usePathname } from "next/navigation";
import { trackingDetails } from "@/api/getOtherFormDetails";
import { useOTPContext } from "@/components/sections/CommonModals/context";
import { DEGREE_TYPES } from "@/components/molecules/ProgramMenu/form-constants";
import { pushToDataLayer, pushToDataLayerDeferred } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { ALL_COUNTRIES } from "@/context/countries";
import { useIpStackDetails } from "@/helpers/useIpStackDetails";
import { fetchIpInfoData, getIpJSONData } from "@/context/ip.storage.utils";
import {
  createLead,
} from "../service/blogConnectModalForm.service";
import {
  SELECT_DEGREE,
  SELECT_PROGRAM,
  filterSelectedPrograms,
  getProgramId,
  precedentSelectedPrograms,
} from "../utils";
import {
  CODE,
  NATIONAL_CODE,
  createBlogConnectModalFormResolver,
  degreeOptions,
  errorAccordingToFieldName,
  regexForInputs,
  type BlogConnectModalFormProps,
  type BlogConnectModalFormValues,
} from "../model/blogConnectModalForm.types";

const IP_INFO_DELAY_MS = 1000;
const LOADER_RESET_MS = 2000;

export interface BlogConnectModalFormViewModel {
  control: Control<BlogConnectModalFormValues>;
  errors: Partial<Record<keyof BlogConnectModalFormValues, string | undefined>>;
  isSubmitting: boolean;
  isOTPLoading: boolean;
  programsList: string[];
  showProgramField: boolean;
  isApplicationLoader: boolean;
  loadingCount: number;
  countries: typeof ALL_COUNTRIES;
  handleFieldChange: (
    fieldName: keyof BlogConnectModalFormValues,
    value: string | boolean,
  ) => void;
  handleDegreeChange: (value: string) => void;
  handleProgramChange: (value: string) => void;
  handlePhoneNumberChange: (value: string) => void;
  handleFieldBlur: (fieldName: string, value: string, error: string) => void;
  updateCountryCode: (value: string) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
  errorAccordingToFieldName: typeof errorAccordingToFieldName;
  degreeOptions: typeof degreeOptions;
  SELECT_DEGREE: string;
  SELECT_PROGRAM: string;
}

export function useBlogConnectModalFormController(
  props: BlogConnectModalFormProps,
): BlogConnectModalFormViewModel {
  const {
    
    setMessage = () => {},
    setFormValuesData = () => {},
    associatedPrograms = [],
    isNewLeadForm = true,
    checkLeadFail = () => {},
  } = props;

  const path = usePathname();
  const isAfricaPage = path == "/africa";
  const { ipStackDetails, countryCode } = useIpStackDetails(isAfricaPage);
  const {
    handleSendOTP,
    isOTPLoading,
    setLeadResponse,
    isHeaderHidden,
    setIsHeaderHidden,
    setShowLoader,
    setIsNewSignInFlow,
    isOTPSent,
  } = useOTPContext();

  const specializationKey = useRef<string>("");
  const custom10PageTypeKey = useRef<string>("");
  const isFiredOnceIpinfo = useRef(false);
  const contactIdRef = useRef<string | null>(null);
  const counterInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const apiResponseURL = useRef<string | null>(null);

  const programListFinal = precedentSelectedPrograms(associatedPrograms);
  const [showProgramField, setShowProgramField] = useState(false);
  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);
  const [isApplicationLoader, setIsApplicationLoader] = useState(false);
  const [loadingCount, setLoadingCount] = useState(3);

  const requireProgramFields = showProgramField || !isNewLeadForm;

  const resolver = useMemo(
    () => createBlogConnectModalFormResolver({ requireProgramFields }),
    [requireProgramFields],
  );

  const defaultFormValues: BlogConnectModalFormValues = {
    fullName: "",
    countryCode: countryCode,
    phoneNumber: "",
    emailId: "",
    degree: SELECT_DEGREE,
    program: SELECT_PROGRAM,
    isTermsAccepted: true,
  };

  const {
    control,
    setValue,
    getValues,
    trigger,
    clearErrors,
    reset,
    handleSubmit: rhfHandleSubmit,
    formState: { errors: rhfErrors, isSubmitting },
  } = useForm<BlogConnectModalFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver,
  });

  useLeadFormValidationReset({
    clearErrors,
    isOTPSent,
    showProgramField: showProgramField,
    isHeaderHidden,
  });

  // Narrow useWatch subscriptions for side-effect effects only.
  // Field values in the view come from per-field <Controller> render props.
  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedEmailId = useWatch({ control, name: "emailId" });
  const watchedFullName = useWatch({ control, name: "fullName" });
  const watchedDegree = useWatch({ control, name: "degree" });

  const isLoading = isSubmitUIBusy || isSubmitting;

  useEffect(() => {
    setIsNewSignInFlow(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue("countryCode", countryCode);
  }, [ipStackDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      (watchedFullName || watchedEmailId || watchedPhoneNumber) &&
      !isFiredOnceIpinfo.current
    ) {
      setTimeout(fetchIpInfoData, IP_INFO_DELAY_MS);
      isFiredOnceIpinfo.current = true;
    }
  }, [watchedFullName, watchedEmailId, watchedPhoneNumber]);

  useEffect(() => {
    if (getValues("phoneNumber")) {
      trigger("phoneNumber");
    }
  }, [watchedCountryCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const programsList = useMemo(
    () =>
      precedentSelectedPrograms(
        filterSelectedPrograms({
          associatedPrograms,
          degree: watchedDegree,
        }),
      ),
    [associatedPrograms, watchedDegree],
  );

  useEffect(() => {
    return () => {
      if (counterInterval.current) clearInterval(counterInterval.current);
    };
  }, []);

  const updateCountryCode = useCallback(
    (value: string) => {
      setValue("countryCode", value, { shouldValidate: true, shouldDirty: true });
      setValue("phoneNumber", "", { shouldDirty: true });
      clearErrors("phoneNumber");
    },
    [setValue, clearErrors],
  );

  const handleFieldBlur = useCallback(
    (fieldName: string, value: string, error: string) => {
      pushToDataLayerDeferred({
        event: "form_filled",
        field_name: fieldName,
        option_selected: value || "NA",
        error_message: error || "NA",
        section_name: getGlobalStateValue("parentSection"),
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });
    },
    [],
  );

  const handleFieldChange = useCallback(
    (
      fieldName: keyof BlogConnectModalFormValues,
      value: string | boolean,
    ) => {
      setLeadFormInputValue(setValue, trigger, fieldName, value as any);

      if (fieldName === "countryCode") {
        trigger("phoneNumber");
      }
    },
    [setValue, trigger],
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      setLeadFormInputValue(setValue, trigger, "phoneNumber", value);
    },
    [setValue, trigger],
  );

  const handleDegreeChange = useCallback(
    (value: string) => {
      setLeadFormDegreeValue(setValue, clearErrors, value, SELECT_PROGRAM);
      const errMsg = degreeOptions.includes(value)
        ? ""
        : errorAccordingToFieldName.degree;
      handleFieldBlur("degree", value, errMsg);
    },
    [setValue, clearErrors, handleFieldBlur],
  );

  const handleProgramChange = useCallback(
    (value: string) => {
      setLeadFormProgramValue(setValue, clearErrors, value, SELECT_PROGRAM);
      handleFieldBlur(
        "program",
        value,
        value !== SELECT_PROGRAM ? "" : errorAccordingToFieldName.program,
      );

      const selectedProgram = associatedPrograms.find(
        (obj) =>
          obj.attributes.BasicDetails?.Heading?.toLocaleUpperCase() === value,
      );

      if (selectedProgram) {
        specializationKey.current =
          selectedProgram.attributes.BasicDetails?.SpecializationText || "";
        custom10PageTypeKey.current =
          selectedProgram.attributes.BasicDetails?.Custom10PgType || "";
      }
    },
    [associatedPrograms, setValue, clearErrors, handleFieldBlur],
  );

  const resetAfterRedirect = useCallback(() => {
    setIsHeaderHidden(false);
    setIsApplicationLoader(false);
    setShowProgramField(false);
    reset({
      fullName: "",
      countryCode: NATIONAL_CODE,
      phoneNumber: "",
      emailId: "",
      degree: SELECT_DEGREE,
      program: SELECT_PROGRAM,
      isTermsAccepted: true,
    });
    setLoadingCount(3);
  }, [reset, setIsHeaderHidden]);

  const startLoader = useCallback(() => {
    setIsApplicationLoader(true);
    counterInterval.current = setInterval(() => {
      setLoadingCount((count) => {
        if (count < 1) {
          if (counterInterval.current) clearInterval(counterInterval.current);
          setTimeout(resetAfterRedirect, LOADER_RESET_MS);
          if (apiResponseURL.current) {
            window.location.href = apiResponseURL.current + "&e=1";
          } else {
            console.error("No Response Url Found");
          }
          return 0;
        }
        return count - 1;
      });
    }, 1000);
  }, [resetAfterRedirect]);

  const submitLead = useCallback(
    (payload: BlogConnectModalFormValues) => {
      setFormValuesData(payload);

      const form: Record<string, any> = {
        name: payload.fullName,
        phone: payload.phoneNumber,
        countryCode: payload.countryCode,
        email: payload.emailId,
        program: payload.program,
        PROGRAM_ID: getProgramId({
          associatedPrograms,
          program: payload.program,
          isLandingPage: true,
        }),
        degree: DEGREE_TYPES[payload.degree]?.name,
        consent: true,
        ContactId: contactIdRef.current || "",
        Custom6: specializationKey.current || "",
      };
      const tracking = trackingDetails();

      const submitCb = () => {
        setIsHeaderHidden(true);
      };

      const verifyCb = () => {
        setShowLoader(false);
        reset({
          fullName: "",
          countryCode: "+" + ipStackDetails?.location?.calling_code || "91",
          phoneNumber: "",
          emailId: "",
          degree: SELECT_DEGREE,
          program: "",
          isTermsAccepted: true,
        });
        startLoader();
      };

      const cb = () =>
        new Promise((resolve, reject) => {
          async function performApiCalls() {
            setIsSubmitUIBusy(true);
            let leadSquareResponse: any;
            try {
              const response = await createLead({
                form,
                tracking,
                ipstack: getIpJSONData(),
                formExtra: {
                  Custom5: "False",
                  Custom10: custom10PageTypeKey.current || "",
                },
              });
              if (typeof response === "string") {
                leadSquareResponse = JSON.parse(response as unknown as string);
              } else {
                leadSquareResponse = await response.json();
              }

              if (leadSquareResponse?.success) {
                const resp = leadSquareResponse?.response || {};
                if (resp?.ContactId) {
                  contactIdRef.current = resp.ContactId;
                }

                apiResponseURL.current = leadSquareResponse.response.URL || "";

                if (typeof setLeadResponse === "function") {
                  setLeadResponse(resp);
                }

                // Blog: always reveal programs on ID==='1' (no length gate).
                if (resp.ID === "1") {
                  setShowProgramField(true);
                  resolve(LEAD_CHECK_SKIP_OTP);
                  return;
                }

                // Blog: force leadResponse ID to "1" on final success.
                if (typeof setLeadResponse === "function") {
                  setLeadResponse({ ...resp, ID: "1" });
                }

                if (props.setFormResponse) {
                  props.setFormResponse({
                    ID: resp?.ID,
                    link: resp?.URL,
                    ProspectId: resp?.ProspectId,
                  });
                }

                pushToDataLayer({
                  event: "lead_submit",
                  cta_text: "Submit",
                  lead_starting_cta: "Apply Now",
                  lead_starting_url: window.location.href || "NA",
                  lead_id: resp?.ProspectId || "NA",
                  section_name: getGlobalStateValue("parentSection"),
                  status: "success",
                  error_message: "NA",
                  page_type: getGlobalStateValue("pageType"),
                  lead_type:
                    resp.ID === "1" ? "lead_submit_1" : "lead_submit_2",
                  page_url: window?.location?.href || "NA",
                });

                // Blog: no WebEngage on success.

                setIsHeaderHidden(true);
                if (typeof props.setMessage === "function") {
                  props.setMessage(
                    `Your request for counselling has been sent ${
                      leadSquareResponse?.isDuplicate ? "already" : ""
                    }, and our counsellor will reach out to you in the next few hours.`,
                  );
                }

                if (payload?.countryCode !== NATIONAL_CODE) {
                  startLoader();
                }
              } else {
                setShowLoader(false);
                setShowProgramField(true);
                resolve(LEAD_CHECK_SKIP_OTP);
                return;
              }
              resolve(leadSquareResponse?.response);
            } catch (error) {
              pushToDataLayer({
                event: "lead_submit",
                cta_text: "Submit",
                lead_starting_cta: "Apply Now",
                lead_starting_url: window.location.href || "NA",
                lead_id: "NA",
                section_name: getGlobalStateValue("parentSection"),
                status: "fail",
                error_message: error || "NA",
                page_type: getGlobalStateValue("pageType"),
                lead_type: "lead_submit_1",
                page_url: window?.location?.href || "NA",
              });
              console.error("LeadSquare API call failed:", error);
              setShowProgramField(true);
              setMessage(error);
              reject(error);
              return;
            } finally {
              setIsSubmitUIBusy(false);
            }
          }

          performApiCalls();
        });

      handleSendOTP(form, cb, submitCb, verifyCb, checkLeadFail);
    },
    [
      associatedPrograms,
      checkLeadFail,
      handleSendOTP,
      ipStackDetails,
      props,
      reset,
      setFormValuesData,
      setIsHeaderHidden,
      setLeadResponse,
      setMessage,
      setShowLoader,
      startLoader,
    ],
  );

  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault?.();
      if (isLoading || isOTPLoading) return;

      try {
        await import("@/helpers/safeSubmit").then((m) =>
          m.submitAfterValidation({
            rhfHandleSubmit,
            startBusy: () => setIsSubmitUIBusy(true),
            onValid: async (values) => {
              setIsSubmitUIBusy(false);
              submitLead(values);
            },
            onInvalid: () => setIsSubmitUIBusy(false),
            event: e,
          }),
        );
      } catch {
        setIsSubmitUIBusy(false);
      }
    },
    [isLoading, isOTPLoading, rhfHandleSubmit, submitLead],
  );


  const errors = {
      fullName: rhfErrors.fullName?.message,
      countryCode: rhfErrors.countryCode?.message,
      phoneNumber: rhfErrors.phoneNumber?.message,
      emailId: rhfErrors.emailId?.message,
      degree: rhfErrors.degree?.message,
      program: rhfErrors.program?.message,
      isTermsAccepted: rhfErrors.isTermsAccepted?.message,
    };

  return {
    control,
    errors,
    isSubmitting: isLoading,
    isOTPLoading,
    programsList,
    showProgramField,
    isApplicationLoader,
    loadingCount,
    countries: ALL_COUNTRIES,
    handleFieldChange,
    handleDegreeChange,
    handleProgramChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    updateCountryCode,
    handleFormSubmit,
    errorAccordingToFieldName,
    degreeOptions,
    SELECT_DEGREE,
    SELECT_PROGRAM,
  };
}
